import React, { useState } from 'react';
import styled from 'styled-components';
import Bookmark from 'components/Page/Toolbox/Bookmark';
import { Row, Col, Tag, Popover, Menu, Dropdown, Button as AntButton } from 'antd';
import { DownOutlined, CheckOutlined } from '@ant-design/icons';
import Cards from 'components/Core/Cards';
import CardsGroup from 'components/Core/Cards/CardsGroup';
import Pagination from 'components/Core/Pagination';
import Button from 'components/Core/Button';
import { SearchInput } from 'components/Core/TextField/SearchInput';
import ResourceHubFeaturedContent from 'components/Sections/ImageCardSections/ResourceHubFeaturedContent';
import { SectionLayout } from 'components/Sections/SectionLayout';
import SearchIcon from 'components/Brand/Icons/SearchIcon';
import FilterIcon from 'components/Brand/Icons/FilterIcon';
import getBreakpointView from 'utils/getBreakpointView';
import { makeBlogUrl } from 'utils/urlHelper';
import { taxonomyString } from 'utils/text';
import './BlogCardGroupSection.less';

const BlogCardGroupSection = ({
  allContent = [],
  hubType,
  featuredContent,
  industries = [],
  topics = [],
  pageSize = 10,
}) => {
  const [allPosts] = useState(allContent);
  const [filteredPosts, setFilteredPosts] = useState(allContent.slice(0, pageSize));
  const [selectedTags, setSelectedTags] = useState([]);
  const [tempTags, setTempTags] = useState([]);
  const [pageInfo, setPageInfo] = useState({
    currentPage: 1,
    numPages: Math.ceil(allPosts.length / pageSize),
  });
  const [selectedSort, setSelectedSort] = useState('Most Recent');
  const [searchTerm, setSearchTerm] = useState();
  const isMobile = getBreakpointView(['xs', 'sm']);
  const [mobilePopupVisible, setMobilePopupVisible] = useState(false);
  const [mobileSearchVisible, setMobileSearchVisible] = useState(false);
  let filterPopover;

  const handleSearch = term => {
    setSearchTerm(term);
    filterSortSearch({ term });
  };

  const showSearch = e => {
    setMobileSearchVisible(true);

    document.addEventListener('mouseup', function(e) {
      const container = document.getElementsByClassName('search-menu-popup')[0];
      if (container && !container.contains(e.target)) {
        setMobileSearchVisible(false);
      }
    });
  };

  const clearClick = () => {
    setSelectedTags([]);
    setTempTags([]);
  };

  const filterSortSearch = ({
    term = searchTerm,
    sort = selectedSort,
    tags = selectedTags,
    page = 1,
  }) => {
    let fPosts =
      tags.length > 0
        ? allPosts.filter(
            post =>
              post?.taxonomy?.industries?.some(x => tags.includes(x)) ||
              post?.taxonomy?.topics?.some(x => tags.includes(x))
          )
        : allPosts;

    if (term) {
      fPosts = fPosts.filter(
        post =>
          post.title?.toLowerCase().includes(term?.toLowerCase()) ||
          post?.excerpt?.excerpt?.toLowerCase().includes(term?.toLowerCase())
      );
    }

    fPosts.sort((a, b) => {
      if (sort === 'Oldest') {
        return new Date(a.publishDateRaw) - new Date(b.publishDateRaw);
      } else {
        return new Date(b.publishDateRaw) - new Date(a.publishDateRaw);
      }
    });

    setFilteredPosts(fPosts.slice((page - 1) * pageSize, page * pageSize));
    setPageInfo({
      currentPage: fPosts.length > 0 ? page : 0,
      numPages: Math.ceil(fPosts.length / pageSize),
    });
    window.scrollTo(0, 0);
  };

  const filterClick = () => {
    const tags = [...tempTags];

    setSelectedTags([...tags]);

    filterSortSearch({ sort: selectedSort, tags });

    setMobilePopupVisible(false);

    if (!isMobile) {
      filterPopover.triggerRef.current.click();
    }
  };

  const menuTagClick = tag => {
    const tags = tagClick(tag);
    filterSortSearch({ sort: selectedSort, tags });
  };

  const tagClick = (tag, setTags = setSelectedTags, tags = selectedTags) => {
    if (!tags.includes(tag)) {
      setTags([...tags, tag]);
      return [...tags, tag];
    } else {
      setTags([...tags.filter(t => t !== tag)]);
      return [...tags.filter(t => t !== tag)];
    }
  };

  const sortClick = sort => {
    setSelectedSort(sort);
    filterSortSearch({ sort, tags: selectedTags });
  };

  const FilterTag = ({ children, ...props }) => (
    <Tag
      className={selectedTags.includes(children) || tempTags.includes(children) ? 'selected' : ''}
      onClick={() => tagClick(children)}
      {...props}
    >
      {children}
    </Tag>
  );

  const FilterMenu = (
    <div className="filter-menu-popup" data-testid="filter-menu">
      <h6>Filter</h6>
      <p>Topics</p>
      <div>
        {topics
          .map(x => x.name)
          .filter(topic => topic != 'All Topics')
          .map(topic => (
            <FilterTag onClick={() => tagClick(topic, setTempTags, tempTags)}>{topic}</FilterTag>
          ))}
      </div>
      <p>Industries</p>
      <div>
        {industries
          .map(x => x.name)
          .filter(topic => topic != 'All Industries')
          .map(industry => (
            <FilterTag onClick={() => tagClick(industry, setTempTags, tempTags)}>
              {industry}
            </FilterTag>
          ))}
      </div>
      <Row justify="end">
        <Col>
          <Button type="white" size="small" shadow={false} onClick={clearClick} shadow="false">
            Clear
          </Button>
          <Button type="white" size="small" shadow={false} onClick={filterClick} shadow="false">
            Confirm
          </Button>
        </Col>
      </Row>
    </div>
  );

  const SearchMenu = (
    <div className="search-menu-popup">
      <SearchInput handleSearch={handleSearch} allowClear={true} value={searchTerm} />
    </div>
  );

  const SortMenu = (options = ['Most Recent', /* 'Most Popular', */ 'Oldest']) => (
    <Menu mode="vertical" selectable="true">
      {options.map(option => (
        <Menu.Item onClick={() => sortClick(option)}>
          {option} {option === selectedSort ? <CheckOutlined /> : ''}
        </Menu.Item>
      ))}
    </Menu>
  );

  const visibleTopics = topics
    .sort((a, b) => b.count - a.count)
    .map(x => x.name)
    .slice(0, 6);

  return (
    <SectionLayout
      className="section-image-card-group--main-container"
      backgroundColor="--blue-fourth"
    >
      <MenuArea data-testid="menu-area">
        <Row gutter={0} align="bottom">
          <Col lg={19} md={19} sm={10} xs={10} className="section-filter">
            <Popover
              content={SearchMenu}
              arrowPointAtCenter
              placement="bottomRight"
              trigger="click"
              {...(isMobile ? { visible: false } : {})}
            >
              <div
                onClick={showSearch}
                data-testid="search-icon-button"
                className={`search-icon-container${searchTerm ? ' selected' : ''}`}
              >
                <SearchIcon height="30" />
              </div>
            </Popover>
            <Popover
              content={FilterMenu}
              arrowPointAtCenter
              placement="bottomRight"
              trigger="click"
              onVisibleChange={() => {
                setTempTags([]);
              }}
              overlayClassName="mobile-overlay"
              {...(isMobile ? { visible: mobilePopupVisible } : {})}
              ref={r => (filterPopover = r)}
            >
              <div
                onClick={() => {
                  setMobilePopupVisible(true);
                }}
                data-testid="filter-icon-button"
                className={`filter-icon-container${selectedTags.length > 0 ? ' selected' : ''}`}
              >
                <FilterIcon />
              </div>
            </Popover>
            <Row className="filter-tag-container" align="middle" gutter="20">
              <Col>
                {visibleTopics.map(topic => (
                  <FilterTag onClick={() => menuTagClick(topic)}>{topic}</FilterTag>
                ))}
              </Col>
            </Row>
          </Col>
          <Col lg={5} md={5} sm={14} xs={14} className="section-sort">
            <fieldset>
              <legend>Sort</legend>
              <Dropdown overlay={SortMenu} placement="bottomLeft" trigger="click">
                <AntButton data-testid="sort-button">
                  {selectedSort} <DownOutlined />
                </AntButton>
              </Dropdown>
            </fieldset>
          </Col>
        </Row>
      </MenuArea>
      {mobileSearchVisible && isMobile && SearchMenu}
      <BlogCardSection>
        {featuredContent && <ResourceHubFeaturedContent featuredContent={featuredContent} />}

        {filteredPosts?.length > 0 && (
          <CardsGroup cols={2}>
            {filteredPosts.map((data, index) => (
              <Bookmark
                data={data}
                title={data?.title}
                subTitle={taxonomyString(data?.taxonomy)}
                type="article"
                url={`/${hubType}/${data.slug}`}
              >
                <Cards
                  type="Image"
                  bodyType="blog"
                  coverImage={data?.featuredImage?.image || data?.featuredImage}
                  category={taxonomyString(data?.taxonomy)}
                  title={data?.title}
                  description={data.excerpt?.excerpt}
                  ctaTitle="Learn more"
                  ctaLink={makeBlogUrl(data.seo?.pageUrl, hubType)}
                  key={index}
                />
              </Bookmark>
            ))}
          </CardsGroup>
        )}
      </BlogCardSection>
      {pageInfo.currentPage > 0 && (
        <Pagination
          template={hubType}
          pageContext={pageInfo}
          onPage={i => filterSortSearch({ page: i })}
        />
      )}
    </SectionLayout>
  );
};

const BlogCardSection = styled.div`
  .section-blog-card--card-container {
    margin-top: 60px;
    margin-bottom: 60px;

    .ant-col {
      .ant-card {
        justify-content: flex-start;

        .ant-card-cover {
          min-height: 321px;
          max-height: 321px;

          @media (max-width: 1200px) {
            min-height: 250px;
            max-height: 250px;
          }

          @media (max-width: 768px) {
            min-height: 215px;
            max-height: 215px;
            .card-cover--image {
              width: 100%;
              padding-bottom: 0;
              min-height: 215px !important;
              max-height: 215px !important;
            }
          }

          @media (max-width: 568px) {
            min-height: 250px;
            max-height: 250px;
          }

          .card-cover--image {
            width: 100%;
            padding-bottom: 56.25%;
            max-height: none;
          }
        }
      }
      @media (max-width: 768px) {
        margin-bottom: 60px;
        padding-left: 10px !important;
        padding-right: 10px !important;
      }

      @media (max-width: 576px) {
        padding-left: 0 !important;
        padding-right: 0 !important;
      }
    }
  }

  .blog-posts--show-more-btn-wrapper {
    text-align: center;
  }
`;

const MenuArea = styled.div`
  .ant-input {
    border-radius: 0;
    border: 1px solid #dae2e6;
    font-size: 20px;
    padding-left: 48px !important;
  }
  .ant-row {
    display: flex;
    align-items: center;
    @media (max-width: 768px) {
      margin-bottom: 40px;
    }
  }
  .ant-col {
    display: flex;
    justify-content: start;
    @media (max-width: 576px) {
      justify-content: center;
    }
  }
  .section-sort {
    justify-content: flex-end !important;
  }
  .section-filter {
    align-items: flex-end;
  }
`;

export default BlogCardGroupSection;
